import { getAuthClient } from './auth';
import { getApiMode } from './previewMode';
import { isPublicPlatform } from './publicPlatforms';
import { useAppStore } from '../stores';

const auth = getAuthClient();

/**
 * Build a your_tour_api URL with the platform parameter for access control.
 *
 * The platform parameter tells the server what type of access this request
 * represents (yt, page, siteSee, kiosk, etc.). Public platforms skip
 * access checks server-side.
 *
 * `featurePlatform` rides alongside it, and is deliberately a separate
 * parameter rather than a different `platform` value. The TourGuide AI
 * brochure genuinely runs with the PIB's public access, so it must keep
 * sending platform=page — but its feature flags live in field_tgaib_settings
 * under a tgaib_ prefix. Folding the two together would either break its
 * access control or give it the PIB's settings; the latter is what was
 * happening (TheVectre/drupal_your_tour#561). It is only sent when it
 * differs from platform, so no other platform's URLs change.
 *
 * Also carries `mode=preview` when the URL asked for preview mode, so every
 * endpoint gets it from one place rather than each call site remembering.
 * The server decides whether to honour it; see utils/previewMode.js.
 *
 * @param {string} endpoint - The API endpoint path (e.g., '/your_tour_api/config/my-slug')
 * @param {object} params - Additional query parameters to include
 * @param {string} platformOverride - Optional platform to use instead of reading from store
 * @returns {string} The full URL with platform parameter
 */
export function buildYourTourApiUrl(endpoint, params = {}, platformOverride = null) {
  const { settings } = useAppStore.getState();

  // Get platform from override, or from the app store, or default to 'yt'
  const platform = platformOverride || settings?.platform || 'yt';

  // Feature-flag scope. Only differs from platform for the TourGuide AI
  // brochure, which runs as 'page' but reads tgaib_* flags.
  const featurePlatform = settings?.featurePlatform;

  // Build query string
  const allParams = {
    ...params,
    platform,
    _format: 'json',
  };

  // Only send it when it actually differs, so every other platform's URLs —
  // and their CDN cache keys — are unchanged.
  if (featurePlatform && featurePlatform !== platform) {
    allParams.featurePlatform = featurePlatform;
  }

  // Only send mode when preview is actually being asked for - adding an empty
  // mode to every URL would fragment the CDN cache for no reason.
  const mode = getApiMode({ ...settings, platform });
  if (mode) {
    allParams.mode = mode;
  }

  const queryString = new URLSearchParams(allParams).toString();

  // Handle endpoints that already have query params
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}${queryString}`;
}

/**
 * Helper to get the current platform from the app store.
 * Useful for components that need to pass platform explicitly.
 *
 * @returns {string} The current platform (defaults to 'yt')
 */
export function getCurrentPlatform() {
  const { settings } = useAppStore.getState();
  return settings?.platform || 'yt';
}

/**
 * Fetch a your_tour_api endpoint, skipping OAuth for public platforms.
 * Stale tokens cause Drupal's OAuth layer to reject requests before the
 * route controller runs, so public platforms use a plain fetch instead.
 */
export async function fetchYourTourApi( url, platform ) {
  if ( isPublicPlatform( platform ) ) {
    const fullUrl = `${process.env.REACT_APP_YOUR_TOUR_CMS_URL}${url}`;
    const res = await fetch( fullUrl, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      headers: new Headers( {
        'X-Consumer-ID': process.env.REACT_APP_DRUPAL_CONSUMER_UUID,
      } ),
    } );
    return res.json();
  }
  return auth.fetchWithAuthentication( url, 'GET', null );
}

export async function getApiData(request, communityNid) {
  const {
    entityType,
    contentType,
    params,
    filter = [],
  } = request;
  const paramsString = new URLSearchParams(params).toString();
  if (communityNid) {
    filter['og_audience.nid'] = communityNid;
  }
  const filterArray = [];
  if (filter && Object.keys(filter).length) {
    Object.entries(filter).forEach(([key, value]) => {
      filterArray.push(`filter[${key}]=${value}`);
    });
  }
  const filterRawString = filterArray.join('&');
  const filterString = filterRawString ? `${filterRawString}&` : '';
  const url = `/jsonapi/${entityType}/${contentType}?${filterString}${paramsString}`;
  const data = await auth.fetchWithAuthentication(url, 'GET', null);
  return data;
}

export async function getNormalizedApiData(
  request,
  // `communityNid = null` sits mid-signature. Moving it last is a real refactor at
  // every call site, so it is deferred rather than bundled into an unrelated change.
  // eslint-disable-next-line default-param-last
  communityNid = null,
  normalizeFunction,
  normalizeFunctionParams = {},
) {
  const data = await getApiData(request, communityNid);
  if (!data) return false;
  const normalizedData = normalizeFunction(data, normalizeFunctionParams);
  return normalizedData;
}

/**
 * @param {Object} request | Info about URL to fetch data from:
 * {
 *    entityType: {string}, // ex: 'node', 'taxonomy_term'
 *    contentType: {string}, // ex: 'photo_gallery', 'level_of_care'
 *    params: {object}, // ex: {
 *     include: {string}, // ex: 'field_cover_photo, field_gallery_photos'
 *    }
 *  }
 * @param {function} normalizeFunction | Function name that normalizes the data
 * @returns {Promise} | Resolves to normalized API data object
 */
export async function getNormalizedApiDataByUuid(
  request,
  normalizeFunction,
  normalizeFunctionParams = {},
) {
  const {
    entityType,
    contentType,
    uuid,
    params,
    fields,
  } = request;
  const paramsString = new URLSearchParams(params).toString();
  const fieldsArray = [];
  // eslint-disable-next-line no-restricted-syntax
  if (fields && Object.keys(fields).length) {
    Object.entries(fields).forEach(([key, value]) => {
      fieldsArray.push(`fields[${key}]=${value}`);
    });
  }
  const fieldsRawString = fieldsArray.join('&');
  const fieldsString = fieldsRawString ? `&${fieldsRawString}` : '';
  const url = `/jsonapi/${entityType}/${contentType}/${uuid}/?${paramsString}${fieldsString}`;

  const data = await auth.fetchWithAuthentication(url, 'GET', null);
  const normalizedData = normalizeFunction(data, normalizeFunctionParams);
  return normalizedData;
}
